import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  SubjectRounded as SubjectRoundedIcon,
  GroupsRounded as GroupsRoundedIcon,
  OfflineBolt as OfflineBoltRoundedIcon,
} from '@mui/icons-material';
import i18n from '@src/languages';
import { formatNumberWithSuffix } from '@src/utils/number';
import Typography from '@src/new-components/Typography';
import vbeeLogo from '@src/assets/logo/logo-vbee.svg';
import verifiedIcon from '@src/assets/icons/verified-rounded.svg';
import Avatar from '@src/new-components/Avatar';
import IconLabel from '@src/new-components/IconLabel';
import { VOICE_PROVIDER } from '@src/constants/voice';
import { VOICE_CLONE_TYPE } from '@src/constants/voiceCloning';
import { COLOR } from '@src/styles/color';
import { getFrameVoiceUrl } from '@src/services/voiceCloning';
import {
  StyledButtonGradientTopVoice,
  StyledVoicePopover,
} from './index.style';

const ranks = {
  rank1: 1,
  rank2: 2,
  rank3: 3,
};
const colorButtonGradient = {
  [ranks.rank1]: {
    border:
      'linear-gradient(271.47deg, #6d4922 2.63%, #ffb34e 54.41%, #ffb34e 99.32%)',
    color:
      'linear-gradient(263.89deg,#6d4922 6.77%,#b6622a 47.53%, #ffb34e 97.98%,#ffce71 156.2%,#ffe48c 200.83%)',
  },
  [ranks.rank2]: {
    border:
      'linear-gradient(271.47deg, #b5b5b2 2.63%, #6b6f6e 47.55%, #4c4f51 99.32%)',
    color:
      'linear-gradient(84.46deg, #494c4e -2.3%, #6a6e6d 33.82%, #9a9b96 63%, #bcbcb9 103.29%, #b5baba 136.63%)',
  },
  [ranks.rank3]: {
    border:
      'linear-gradient(84.42deg, #EDB48A -2.85%, #AA5A37 62.38%, #8B381F 97.93%)',
    color:
      'linear-gradient(84.42deg, #EDB48A -2.85%, #AA5A37 62.38%, #8B381F 97.93%)',
  },
};

const VoicePopover = ({ voice, retentionDaysLabel, isVbee, prizes }) => {
  const { t } = useTranslation();
  const { language } = i18n;

  const voiceCodeInPrizesEmerging = prizes?.emerging?.find(
    (prize) => prize.awardedVoice?.voiceCode === voice?.code,
  );
  const voiceCodeInPrizesTrending = prizes?.trending?.find(
    (prize) => prize.awardedVoice?.voiceCode === voice?.code,
  );

  const getNumberWithSuffix = (number) =>
    formatNumberWithSuffix(number, language);

  const BadgeIcon = () => {
    if (
      voice?.provider &&
      voice?.provider !== VOICE_PROVIDER.VBEE_VOICE_CLONING
    ) {
      return (
        <img src={vbeeLogo} alt="vbee-logo" className="avatar-badge-icon" />
      );
    }

    if (voice?.type === VOICE_CLONE_TYPE.ZERO_SHOT) {
      return (
        <OfflineBoltRoundedIcon
          className="avatar-badge-icon"
          sx={{
            color: COLOR.darkBlue,
            backgroundColor: COLOR.white,
            borderRadius: '50%',
          }}
        />
      );
    }

    return (
      <img
        src={verifiedIcon}
        alt="pro-voice-cloning"
        className="avatar-badge-icon"
      />
    );
  };
  const handleStopEventClick = (e) => {
    e.stopPropagation();
  };
  return (
    <StyledVoicePopover onClick={handleStopEventClick}>
      <div className="popover-header">
        <Avatar
          avatar={voice.avatar || voice.image || voice.roundImage}
          size={40}
          audioUrl={voice?.demo}
          audioIconColor={COLOR.yellow.default}
          badgeIcon={<BadgeIcon />}
          frameImageUrl={getFrameVoiceUrl(voice)}
          styleBadgeIcon={{ height: '16px !important' }}
        />
        <Typography variant="body-16-medium" color={COLOR.label2[100]}>
          {voice.name}
        </Typography>
      </div>
      <div className="button-gradient-top-container">
        {voiceCodeInPrizesTrending && (
          <StyledButtonGradientTopVoice
            color={
              colorButtonGradient?.[voiceCodeInPrizesTrending?.rank]?.color
            }
            border={
              colorButtonGradient?.[voiceCodeInPrizesTrending?.rank]?.border
            }
          >
            {t('topAllCommunityVoices', {
              rank: voiceCodeInPrizesTrending?.rank,
            })}
          </StyledButtonGradientTopVoice>
        )}
        {voiceCodeInPrizesEmerging && (
          <StyledButtonGradientTopVoice
            color={
              colorButtonGradient?.[voiceCodeInPrizesEmerging?.rank]?.color
            }
            border={
              colorButtonGradient?.[voiceCodeInPrizesEmerging?.rank]?.border
            }
          >
            {t('topEmergingVoice', { rank: voiceCodeInPrizesEmerging?.rank })}
          </StyledButtonGradientTopVoice>
        )}
      </div>
      <Typography
        className="popover-description"
        variant="body-14-regular"
        color={COLOR.label2[64]}
      >
        {voice?.description || t('noVoiceDescription')}
      </Typography>
      <div className="popover-stat">
        {(voice?.stats || retentionDaysLabel) && (
          <>
            {!isVbee && (
              <>
                <IconLabel
                  icon={
                    <GroupsRoundedIcon
                      sx={{ color: COLOR.indigo[64], fontSize: 16 }}
                    />
                  }
                  value={`${getNumberWithSuffix(
                    voice?.stats?.totalUsers ?? 0,
                  )} ${t('numUsersUseVoice')}`}
                  textVariant="body-14-regular"
                  textColor={COLOR.label2[64]}
                />
                <IconLabel
                  icon={
                    <SubjectRoundedIcon
                      sx={{
                        color: COLOR.indigo[64],
                        fontSize: 16,
                      }}
                    />
                  }
                  value={`${getNumberWithSuffix(
                    voice?.stats?.totalCredits ?? 0,
                  )} ${t('numCharactersConverted')}`}
                  textVariant="body-14-regular"
                  textColor={COLOR.label2[64]}
                />
              </>
            )}
            {retentionDaysLabel && (
              <div className="popover-retention-days-label">
                <IconLabel
                  icon={retentionDaysLabel?.icon}
                  value={retentionDaysLabel?.titlePopover}
                  customStyle={{
                    minWidth: '70px',
                  }}
                  textVariant="body-14-regular"
                  textColor={
                    retentionDaysLabel?.isVoiceAboutToBeRemoved
                      ? retentionDaysLabel?.textColor
                      : COLOR.label2[64]
                  }
                />
                {retentionDaysLabel?.isVoiceAboutToBeRemoved && (
                  <Typography
                    variant="body-14-regular"
                    color={COLOR.label2[64]}
                  >
                    {retentionDaysLabel?.contentTooltip}
                  </Typography>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </StyledVoicePopover>
  );
};

export default VoicePopover;
